import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api } from "../../api";
import { Alert, Card } from "../../shell/ui";

export type LimitKey = "bots" | "strategies" | "savedRuns" | "computePerDay";
export type LimitValues = Partial<Record<LimitKey, number>>;
type FullLimits = Record<LimitKey, number>;

/** The API sends and stores "no limit" as -1. */
const UNLIMITED = -1;

interface LimitsConfig {
  keys: LimitKey[];
  labels: Record<LimitKey, string>;
  maxValue: number;
  defaults: Record<"starter" | "pro", FullLimits>;
  roles: Record<"starter" | "pro", FullLimits>;
}

const TITLES: Record<LimitKey, { name: string; hint: string }> = {
  bots: { name: "Paper bots", hint: "Bots running at the same time, per member." },
  strategies: { name: "Strategies", hint: "Saved in their library, including forks and copies." },
  savedRuns: { name: "Saved backtests", hint: "Backtests kept in their history." },
  computePerDay: {
    name: "Backtest runs a day",
    hint: "Saves, sweeps, walk-forward and robustness runs. Resets at midnight UTC.",
  },
};

const KEYS: LimitKey[] = ["bots", "strategies", "savedRuns", "computePerDay"];

export function useLimitsConfig() {
  return useQuery({ queryKey: ["admin", "limits"], queryFn: () => api<LimitsConfig>("/admin/limits") });
}

export const showLimit = (n: number): string => (n === UNLIMITED ? "unlimited" : String(n));

/** One limit's input: a number, or the Unlimited box. */
interface Cell {
  value: string;
  unlimited: boolean;
}

function LimitCell({
  cell,
  onChange,
  placeholder,
  max,
  label,
}: {
  cell: Cell;
  onChange: (c: Cell) => void;
  placeholder?: string;
  max: number;
  label: string;
}) {
  return (
    <div className="limit-cell">
      <input
        type="number"
        min={0}
        max={max}
        step={1}
        inputMode="numeric"
        value={cell.unlimited ? "" : cell.value}
        placeholder={cell.unlimited ? "∞" : placeholder}
        disabled={cell.unlimited}
        onChange={(e) => onChange({ value: e.target.value, unlimited: false })}
        aria-label={label}
      />
      <label className="checkbox">
        <input
          type="checkbox"
          checked={cell.unlimited}
          onChange={(e) => onChange({ value: cell.value, unlimited: e.target.checked })}
        />
        Unlimited
      </label>
    </div>
  );
}

const toCell = (v: number | undefined): Cell => ({
  value: v != null && v !== UNLIMITED ? String(v) : "",
  unlimited: v === UNLIMITED,
});

/** A cell becomes a number, `-1` for unlimited, or `null` (not set: the next layer up applies). */
const fromCell = (c: Cell): number | null =>
  c.unlimited ? UNLIMITED : c.value.trim() === "" ? null : Number(c.value);

type Draft = Record<"starter" | "pro", Record<LimitKey, Cell>>;

function makeDraft(roles: Record<"starter" | "pro", FullLimits>): Draft {
  const one = (r: FullLimits) =>
    Object.fromEntries(KEYS.map((k) => [k, toCell(r[k])])) as Record<LimitKey, Cell>;
  return { starter: one(roles.starter), pro: one(roles.pro) };
}

/** All boxes empty: saving that puts every limit back to its built-in default. */
const emptyDraft = (): Draft => {
  const one = () => Object.fromEntries(KEYS.map((k) => [k, toCell(undefined)])) as Record<LimitKey, Cell>;
  return { starter: one(), pro: one() };
};

function LimitsEditor({ config }: { config: LimitsConfig }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Draft>(() => makeDraft(config.roles));
  const save = useMutation({
    mutationFn: () => {
      const body = Object.fromEntries(
        (["starter", "pro"] as const).map((role) => [
          role,
          Object.fromEntries(KEYS.map((k) => [k, fromCell(draft[role][k])])),
        ]),
      );
      return api("/admin/limits", { method: "PUT", body });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin"] }),
  });
  const set = (role: "starter" | "pro", key: LimitKey, cell: Cell) =>
    setDraft((d) => ({ ...d, [role]: { ...d[role], [key]: cell } }));

  return (
    <Card
      title="Limits by level"
      actions={
        <span className="row">
          <button type="button" className="btn btn--sm" onClick={() => setDraft(emptyDraft())}>
            Back to defaults
          </button>
          <button
            type="button"
            className="btn btn--primary btn--sm"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? "Saving…" : "Save changes"}
          </button>
        </span>
      }
    >
      {save.error && <Alert tone="error">{save.error.message}</Alert>}
      {save.isSuccess && <Alert tone="ok">Saved. The new limits apply from the next action.</Alert>}
      <div className="table-wrap">
        <table className="limits-table">
          <thead>
            <tr>
              <th>Limit</th>
              <th>Starter</th>
              <th>Pro</th>
            </tr>
          </thead>
          <tbody>
            {KEYS.map((k) => (
              <tr key={k}>
                <td>
                  <strong>{TITLES[k].name}</strong>
                  <div className="muted small">{TITLES[k].hint}</div>
                </td>
                {(["starter", "pro"] as const).map((role) => (
                  <td key={role}>
                    <LimitCell
                      cell={draft[role][k]}
                      onChange={(c) => set(role, k, c)}
                      placeholder={showLimit(config.defaults[role][k])}
                      max={config.maxValue}
                      label={`${role} ${TITLES[k].name}`}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="muted small">
        An empty box means the built-in default shown in grey; "Back to defaults" empties every box, and Save
        applies it. Admins have no limits. Lowering a limit never deletes anything: a member who is over the
        new limit just can't add more. To give one member more or less than their level, open them on the
        Members tab and press Limits.
      </p>
    </Card>
  );
}

export function LimitsTab() {
  const config = useLimitsConfig();
  if (!config.data) return <p className="muted">Loading…</p>;
  return <LimitsEditor config={config.data} />;
}

/** One member's own limits, shown under their row on the Members tab. */
export function MemberLimits({
  userId,
  overrides,
  limits,
  usage,
}: {
  userId: string;
  overrides: LimitValues;
  limits: FullLimits | null;
  usage: FullLimits;
}) {
  const qc = useQueryClient();
  const config = useLimitsConfig();
  const [draft, setDraft] = useState<Record<LimitKey, Cell>>(
    () => Object.fromEntries(KEYS.map((k) => [k, toCell(overrides[k])])) as Record<LimitKey, Cell>,
  );
  const save = useMutation({
    mutationFn: () =>
      api(`/admin/users/${userId}/limits`, {
        method: "PATCH",
        body: { limits: Object.fromEntries(KEYS.map((k) => [k, fromCell(draft[k])])) },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin"] }),
  });
  if (!limits) return <p className="muted small">Admins have no limits.</p>;
  return (
    <div className="stack">
      <p className="muted small">
        In use now:{" "}
        {KEYS.map((k) => `${usage[k]} of ${showLimit(limits[k])} ${TITLES[k].name.toLowerCase()}`).join(
          " · ",
        )}
      </p>
      {save.error && <Alert tone="error">{save.error.message}</Alert>}
      {save.isSuccess && <Alert tone="ok">Saved.</Alert>}
      <div className="limits-grid">
        {KEYS.map((k) => (
          <div key={k} className="limits-field">
            <span>{TITLES[k].name}</span>
            <LimitCell
              cell={draft[k]}
              onChange={(c) => setDraft((d) => ({ ...d, [k]: c }))}
              placeholder={`level: ${showLimit(limits[k])}`}
              max={config.data?.maxValue ?? 1_000_000}
              label={TITLES[k].name}
            />
          </div>
        ))}
      </div>
      <div>
        <button
          type="button"
          className="btn btn--primary btn--sm"
          disabled={save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? "Saving…" : "Save this member's limits"}
        </button>{" "}
        <span className="muted small">An empty box uses the limit of their level.</span>
      </div>
    </div>
  );
}
